// /composables/useVideoAds.js
import { replaceMacrosDynamically } from "../utils/replaceMacros";
import { analyticsEvent } from "../utils/analytics";

const AD_TIME_DELTA_SECONDS = 180;
const AD_RETRY_INTERVAL_MS = 1000;
const IMA_SDK_SRC = "https://imasdk.googleapis.com/js/sdkloader/ima3.js";
let imaReadyPromise = null;

function ensureImaReady() {
  if (imaReadyPromise) return imaReadyPromise;

  imaReadyPromise = (async () => {
    if (!globalThis?.google?.ima) {
      await new Promise((resolve, reject) => {
        const existingScript = document.querySelector(`script[src="${IMA_SDK_SRC}"]`);
        if (existingScript) {
          if (globalThis?.google?.ima) {
            resolve();
            return;
          }
          existingScript.addEventListener("load", resolve, { once: true });
          existingScript.addEventListener("error", reject, { once: true });
          return;
        }

        const script = document.createElement("script");
        script.src = IMA_SDK_SRC;
        script.async = true;
        script.addEventListener("load", resolve, { once: true });
        script.addEventListener("error", reject, { once: true });
        document.head.appendChild(script);
      });
    }

    await import("videojs-contrib-ads");
    await import("videojs-ima");
  })().catch((error) => {
    imaReadyPromise = null;
    throw error;
  });

  return imaReadyPromise;
}

export function useVideoAds(player, video, videoPlayerEl) {
  const fetchedAds = new Set();
  const playedAds = new Set();
  let adTagIndex = 0;
  let fallbackVastUrls = [];
  let quartiles = {
    percent_25: false,
    percent_50: false,
    percent_75: false
  };
  let hasPendingAd = true;
  let isRequestingAd = false;
  let adFetchRetryInterval = null;
  let adFetchSuccess = false;
  let currentAdBreakTarget = null;

  function parseHHMMSStoSeconds(timeString) {
    const [hh, mm, ss] = timeString.split(":").map(Number);
    return hh * 3600 + mm * 60 + ss;
  }

  function getAdBreakTimesInSeconds() {
    const videosAdBreaks = video.value?.content?.adBreaks
      ?.map(parseHHMMSStoSeconds)
      ?.filter((time) => time > 0);
    if (videosAdBreaks.length > 0 && !videosAdBreaks.includes(0)) {
      videosAdBreaks.unshift(0);
    }
    return videosAdBreaks.length
      ? videosAdBreaks
      : [0, video.value?.content?.duration];
  }

  function applyAdTimeDelta(adBreakTimes = []) {
    return adBreakTimes.map((time) => Math.max(time - AD_TIME_DELTA_SECONDS, 0));
  }

  async function initIMAAds(cacheUrls, autoplay = false, skip = false, adBreaks = []) {
    if (!player.value) return;
    await ensureImaReady();

    adTagIndex = 0;
    playedAds.clear();
    fallbackVastUrls = video.value?.content?.vastTagUrl || [];
    fallbackVastUrls = [
      ...fallbackVastUrls.map((url) =>
        replaceMacrosDynamically(
          url,
          player.value.width(),
          player.value.height(),
          autoplay,
          player.value.muted(),
          skip,
          adBreaks
        )
      ),
      ...cacheUrls.map(replaceMacrosDynamically)
    ];

    player.value.ima({
      id: videoPlayerEl.value.id,
      adLabel: "",
      adBreakTimes: applyAdTimeDelta(getAdBreakTimesInSeconds()),
      autoPlayAdBreaks: false
    });

    player.value.on("adsready", () => {
      // We only want to play the preroll directly after fetching, but for midrolls we want to choose the timing
      console.log("[ADS-READY] Ads ready");
      if (hasPendingAd) {
        hasPendingAd = false;
        player.value.ima.playAdBreak();
      }
    });

    player.value.on("ads-ad-ended", () => {
      player.value.ima.adsLoader.contentComplete();
    });

    player.value.on("ads-request", () => {
      const adBreaks = getAdBreakTimesInSeconds();
      const currentTime = Math.floor(player.value.currentTime());
      const skippedAdBreak = adBreaks.find((time) => currentTime > time && !playedAds.has(time));
      console.log("Skipped ad break", skippedAdBreak);
      if (skippedAdBreak) {
        hasPendingAd = true;
      }
    });

    player.value.on("ads-manager", (manager) => {
      manager.adsManager.addEventListener("loaded", () => {
        const currentTime = Math.floor(player.value.currentTime());
        const adType = currentTime < 1 ? "preroll" : "midroll";
        analyticsEvent(video.value.title, "ad", `${adType}_ad_break_filled`);
        isRequestingAd = false;
        adFetchSuccess = true;
        stopAdFetchRetryLoop();
      });
    })

    player.value.on("adserror", (error) => {
      const currentTime = Math.floor(player.value.currentTime());
      const adType = currentTime < 1 ? "preroll" : "midroll";
      analyticsEvent(video.value.title, "ad", `${adType}_ad_break_notfilled`);

      console.error(error);
      isRequestingAd = false;
      adTagIndex++;

      if (adTagIndex < fallbackVastUrls.length) {
       getAd();
      }
    });
  }

  async function handleTimeUpdate() {
    const currentTime = Math.floor(player.value.currentTime());
    const duration = player.value.duration();

    if (!quartiles.percent_25 && currentTime >= duration / 4) {
      analyticsEvent(video.value.title, "beacon", "25_quartile");
      quartiles.percent_25 = true;
    }
    if (!quartiles.percent_50 && currentTime >= duration / 2) {
      analyticsEvent(video.value.title, "beacon", "50_quartile");
      quartiles.percent_50 = true;
    }
    if (!quartiles.percent_75 && currentTime >= duration / 4 * 3) {
      analyticsEvent(video.value.title, "beacon", "75_quartile");
      quartiles.percent_75 = true;
    }

    const adBreaks = getAdBreakTimesInSeconds();
    const adBreaksWithDelta = applyAdTimeDelta(adBreaks);

    const unfetchedBreaks = adBreaksWithDelta.filter(
      (time) => time > 0 && time <= currentTime && !fetchedAds.has(time)
    );
    if (unfetchedBreaks.length > 0 && !adFetchRetryInterval) {
      unfetchedBreaks.forEach((time) => fetchedAds.add(time));
      const targetAdBreak = adBreaks.find((breakTime) =>
        unfetchedBreaks.includes(Math.max(breakTime - AD_TIME_DELTA_SECONDS, 0))
      );
      startAdFetchRetryLoop(targetAdBreak);
    }

    const unplayedBreaks = adBreaks.filter(
      (time) => time > 0 && time <= currentTime && !playedAds.has(time)
    );
    if (unplayedBreaks.length > 0) {
      unplayedBreaks.forEach((time) => playedAds.add(time));

      if (hasPendingAd) {
        playAd();
      } else {
        adTagIndex = 0;
        hasPendingAd = true;
        await getAd();
      }
    }
  }

  async function playPreRollAd() {
    const adBreaks = getAdBreakTimesInSeconds();
    if (!adBreaks.includes(0)) return;

    playedAds.add(0);
    return getAd();
  }

  async function getAd() {
    if (video.value.is_rented) {
      console.log("Video is rented, not playing ad");
      return;
    }

    if (!player.value?.ima) return;

    if (fallbackVastUrls.length > 0 && !isRequestingAd) {
      isRequestingAd = true;
      try {
        const newUrl = fallbackVastUrls[adTagIndex];
        player.value.ima.changeAdTag(newUrl);
        await player.value.ima.requestAds();
      } catch (error) {
        adTagIndex = 0;
        isRequestingAd = false;
        console.log("🔥 Error getting fallback ad", error);
      }
    }
  }

  async function playAd() {
    if (!player.value?.ima) {
      console.log("IMA not ready, not playing ad");
      return;
    };
    player.value.ima.playAdBreak();
  }

  function stopAdFetchRetryLoop() {
    if (adFetchRetryInterval) {
      clearInterval(adFetchRetryInterval);
      adFetchRetryInterval = null;
    }
    currentAdBreakTarget = null;
  }

  function startAdFetchRetryLoop(adBreakTime) {
    stopAdFetchRetryLoop();
    currentAdBreakTarget = adBreakTime;
    adFetchSuccess = false;
    adTagIndex = 0;

    const attemptAdFetch = async () => {
      if (adFetchSuccess) {
        stopAdFetchRetryLoop();
        return;
      }

      const currentTime = Math.floor(player.value.currentTime());
      if (currentAdBreakTarget && currentTime >= currentAdBreakTarget) {
        stopAdFetchRetryLoop();
        return;
      }

      if (!isRequestingAd) {
        adTagIndex = 0;
        await getAd();
      }
    };

    attemptAdFetch();
    adFetchRetryInterval = setInterval(attemptAdFetch, AD_RETRY_INTERVAL_MS);
  }

  return {
    parseHHMMSStoSeconds,
    initIMAAds,
    handleTimeUpdate,
    getAd,
    playPreRollAd,
    playAd
  };
}
